import { WfnMultiSelect } from '@adp-wfn/mdf-wc-react';
import { SdfCheckbox, SdfCheckboxGroup } from '@waypoint/react-components';
import React, { useCallback, useMemo } from 'react';
import { BaseField } from './BaseField';
import { useFieldLogic } from './hooks';
import { Field } from './types';

/**
 * Multi-select field component (checkboxes or multi-select dropdown)
 * @param props - Field component props
 * @param props.data - Field configuration with options and values array
 * @param props.onChange - Callback function for selection changes
 * @param props.isHorizontal - Whether to render checkboxes horizontally
 * @param props.renderPreference - Rendering preference (compact/expanded/auto)
 * @returns JSX element for multi-select field
 */
export function SomeOf({ data, onChange, isHorizontal, renderPreference = 'auto' }: Field) {
  const { useCompactControl } = useFieldLogic(data.options, renderPreference, 4);

  const dropdownData = useMemo(() => {
    if (!data.options || !Array.isArray(data.options)) {
      return [];
    }

    return data.options.map((item) => {
      return {
        ...item,
        label: item.label ?? item.text
      };
    });
  }, [data.options]);

  const handleChange = useCallback(
    (event: any) => onChange(event?.detail?.map((d) => d.value) || []),
    [onChange]
  );

  const handleCheckboxChange = useCallback(
    (event: any) => onChange(event.detail),
    [onChange]
  );

  // temporary workaround to pass onSdfChange prop until WFNSUPP-240400 is resolved
  const props = {
    onSdfChange: handleChange
  };

  if (useCompactControl) {
    return (
      <BaseField>
        <WfnMultiSelect
          {...props}
          data={dropdownData}
          label={data.label}
          tooltipText={data.tooltipText}
          value={data.value}
        />
      </BaseField>
    );
  }

  return (
    <BaseField>
      <SdfCheckboxGroup
        direction={isHorizontal ? 'row' : 'column'}
        label={data.label}
        tooltipText={data.tooltipText}
        value={data.value || []}
        onSdfChange={handleCheckboxChange}
      >
        {data.options?.map((item) => (
          <SdfCheckbox
            key={item.id}
            label={item.label || item.text}
            value={item.value}
          />
        ))}
      </SdfCheckboxGroup>
    </BaseField>
  );
}
